import { useEffect } from 'react';

export type ToastType = 'error' | 'success' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastItemProps {
  toast: Toast;
  onClose: (id: string) => void;
}

export function ToastItem({ toast, onClose }: ToastItemProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(toast.id);
    }, 5000);

    return () => clearTimeout(timer);
  }, [toast.id, onClose]);

  const bgColor = {
    error: 'bg-red-900/90 border-red-800',
    success: 'bg-green-900/90 border-green-800',
    info: 'bg-blue-900/90 border-blue-800',
    warning: 'bg-yellow-900/90 border-yellow-800',
  }[toast.type];

  const textColor = {
    error: 'text-red-200',
    success: 'text-green-200',
    info: 'text-blue-200',
    warning: 'text-yellow-200',
  }[toast.type];

  return (
    <div
      className={`${bgColor} ${textColor} border rounded-lg shadow-lg p-4 mb-3 min-w-[300px] max-w-[400px] animate-slide-in`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm flex-1">{toast.message}</p>
        <button
          onClick={() => onClose(toast.id)}
          className="text-slate-400 hover:text-slate-200 transition-colors flex-shrink-0"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  onClose: (id: string) => void;
}

export function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 pointer-events-none">
      <div className="pointer-events-auto">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={onClose} />
        ))}
      </div>
    </div>
  );
}
